import apiClient from './apiClient';
import API_ENDPOINTS from '../Api/endpoints';

/**
 * Reports service: document report email
 */
const genCorrelationId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Fallback simple generator
  return 'cid-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
};

export const sendDocumentReportEmail = async ({ organizationId, days, recipients, summaryOnly, documentTypes, status, reportType }) => {
  // recipients: string[] or comma-separated -> ensure array of trimmed emails
  const toList = Array.isArray(recipients)
    ? recipients
    : String(recipients || '')
        .split(',')
        .map((e) => e.trim())
        .filter((e) => !!e);

  const payload = {
    organizationId,
    days,
    recipients: toList,
    includeSummaryOnly: !!summaryOnly,
    documentTypes: Array.isArray(documentTypes) ? documentTypes : [],
  };
  if (status !== undefined && status !== null && status !== '') {
    payload.status = Array.isArray(status) ? status : [status];
  }
  if (reportType !== undefined && reportType !== null && reportType !== '') {
    payload.reportType = reportType;
  }

  const correlationId = genCorrelationId();
  // Avoid custom headers to prevent CORS preflight in browser environments
  const { data } = await apiClient.post(API_ENDPOINTS.REPORTS.DOCUMENTS.EMAIL, payload);
  return { data, correlationId };
};

export const fetchDocumentTypes = async () => {
  const { data } = await apiClient.get(API_ENDPOINTS.DOCUMENT_PROCESSING.TYPES);
  // Existing endpoint retained for other screens. For DocumentReport, use fetchReportDocumentTypes.
  return data;
};

// Org-specific document types for Document Report dropdown
export const fetchReportDocumentTypes = async (organizationId) => {
  const qp = new URLSearchParams();
  if (organizationId) qp.append('organizationId', organizationId);
  const url = `${API_ENDPOINTS.REPORTS.DOCUMENTS.TYPES}?${qp.toString()}`;
  const { data } = await apiClient.get(url);
  return data || [];
};

export const listDocumentsReport = async (params = {}) => {
  // params: { page, size, fromDate, toDate, days, types (array|string), isCurrent, orgId|organizationId }
  // Avoid sending custom headers on GET to prevent CORS preflight
  const correlationId = genCorrelationId();
  const qp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    if (k === 'types') {
      const arr = Array.isArray(v) ? v : String(v).split(',').map((x) => x.trim()).filter(Boolean);
      if (arr.length) qp.append('types', arr.join(','));
    } else {
      qp.append(k, v);
    }
  });
  const url = `${API_ENDPOINTS.REPORTS.DOCUMENTS.LIST}?${qp.toString()}`;
  try { console.debug('[ReportsAPI] GET', url); } catch {}
  const { data } = await apiClient.get(url);
  return { data, correlationId };
};

export const exportDocumentsReportCsv = async (params = {}) => {
  const correlationId = genCorrelationId();
  const qp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    if (k === 'types') {
      const arr = Array.isArray(v) ? v : String(v).split(',').map((x) => x.trim()).filter(Boolean);
      if (arr.length) qp.append('types', arr.join(','));
    } else {
      qp.append(k, v);
    }
  });
  const url = `${API_ENDPOINTS.REPORTS.DOCUMENTS.EXPORT}?${qp.toString()}`;
  try { console.debug('[ReportsAPI] GET (export)', url); } catch {}
  const resp = await apiClient.get(url, { responseType: 'blob' });
  return { blob: resp.data, correlationId };
};

export const fetchDocumentsSummary = async (params = {}) => {
  const correlationId = genCorrelationId();
  const qp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    if (k === 'types') {
      const arr = Array.isArray(v) ? v : String(v).split(',').map((x) => x.trim()).filter(Boolean);
      if (arr.length) qp.append('types', arr.join(','));
    } else {
      qp.append(k, v);
    }
  });
  const url = `${API_ENDPOINTS.REPORTS.DOCUMENTS.SUMMARY}?${qp.toString()}`;
  try { console.debug('[ReportsAPI] GET (summary)', url); } catch {}
  const { data } = await apiClient.get(url);
  return { data, correlationId };
};

export const listMissingDocuments = async (params = {}) => {
  // params: { page, size, organizationId, documentType }
  const correlationId = genCorrelationId();
  const qp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    qp.append(k, v);
  });
  const url = `${API_ENDPOINTS.REPORTS.MISSING_DOCUMENTS.LIST}?${qp.toString()}`;
  try { console.debug('[ReportsAPI] GET (missing-documents)', url); } catch {}
  const { data } = await apiClient.get(url);
  return { data, correlationId };
};

export const listStorageUsage = async (params = {}) => {
  // params: { search, page, size }
  const correlationId = genCorrelationId();
  const qp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    qp.append(k, v);
  });
  const url = `${API_ENDPOINTS.REPORTS.STORAGE.LIST}?${qp.toString()}`;
  const { data } = await apiClient.get(url);
  return { data, correlationId };
};

export const recomputeStorageUsage = async () => {
  const { data } = await apiClient.post(API_ENDPOINTS.REPORTS.STORAGE.RECOMPUTE);
  return { data };
};

export const fetchMissingDocumentsSummary = async (params = {}) => {
  // params: { organizationId, documentType }
  const correlationId = genCorrelationId();
  const qp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    qp.append(k, v);
  });
  const url = `${API_ENDPOINTS.REPORTS.MISSING_DOCUMENTS.SUMMARY}?${qp.toString()}`;
  try { console.debug('[ReportsAPI] GET (missing-documents-summary)', url); } catch {}
  const { data } = await apiClient.get(url);
  return { data, correlationId };
};

export const exportMissingDocumentsCsv = async (params = {}) => {
  // params: { organizationId, documentType }
  const correlationId = genCorrelationId();
  const qp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    qp.append(k, v);
  });
  const url = `${API_ENDPOINTS.REPORTS.MISSING_DOCUMENTS.EXPORT}?${qp.toString()}`;
  try { console.debug('[ReportsAPI] GET (missing-documents-export)', url); } catch {}
  const resp = await apiClient.get(url, { responseType: 'blob' });
  return { blob: resp.data, correlationId };
};
